import React, { useState } from 'react';
import ChevronDownIcon from '@atlaskit/icon/core/migration/chevron-down';
import ChevronUpIcon from '@atlaskit/icon/core/migration/chevron-up';
import { Text, Flex, xcss, Stack, Inline, Box, Pressable } from '@atlaskit/primitives';
import { JiraServiceManagementLogo } from '@atlaskit/temp-nav-app-icons/jira-service-management';
import Heading from '@atlaskit/heading';

import { PremiumLandingPageEntryPointButtonWithDrawer } from '@atlassian/jira-servicedesk-premium-landing-page-buttons/src/ui/index.tsx';
import { fireUIAnalytics, type UIAnalyticsEvent } from '@atlassian/jira-product-analytics-bridge';
import { useIntl } from '@atlassian/jira-intl';
import type { PremiumLandingPageOrigin } from '@atlassian/jira-servicedesk-premium-landing-page/src/ui/types.tsx';
import { UpsellButton } from '@atlassian/jira-servicedesk-premium-landing-page/src/ui/upsell-button/index.tsx';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/JSErrorBoundary.tsx';
import { messages } from './messages.tsx';

type PremiumLandingPageTriggerBannerProps = {
	origin: PremiumLandingPageOrigin;
};

export const PremiumLandingPageTriggerBanner = ({
	origin,
}: PremiumLandingPageTriggerBannerProps) => {
	const isAccordionEnabled = origin === 'IN_PRODUCT_HELP';
	const { formatMessage } = useIntl();
	const [isOpen, setIsOpen] = useState(true);

	const toggleAccordion = (analyticsEvent: UIAnalyticsEvent) => {
		setIsOpen(!isOpen);
		fireUIAnalytics(analyticsEvent, 'premiumLandingPageTriggerBannerAccordionToggle', {
			toggleAction: isOpen ? 'close' : 'open',
			landingPageOrigin: origin,
		});
	};

	const mainContent = () => (
		<Stack space="space.150" xcss={contentWrapperStyles}>
			<JiraServiceManagementLogo />
			<Inline space="space.075" alignBlock="start" alignInline="start">
				<Box xcss={headingStyles}>
					<Heading as="h2" size="xsmall">
						{formatMessage(messages.subtitleLabel)}
					</Heading>
				</Box>
				{isAccordionEnabled &&
					(isOpen ? (
						<ChevronUpIcon
							spacing="spacious"
							label={formatMessage(messages.bannerAccordionCollapse)}
							size="medium"
						/>
					) : (
						<ChevronDownIcon
							spacing="spacious"
							label={formatMessage(messages.bannerAccordionExpand)}
							size="medium"
						/>
					))}
			</Inline>
			{isOpen && (
				<Box xcss={descriptionTextStyles}>
					<Text color="color.text.subtle">{formatMessage(messages.descriptionLabel)}</Text>
				</Box>
			)}
			{isOpen && (
				<Flex justifyContent="end" gap="space.100" xcss={buttonsWrapperStyles}>
					<PremiumLandingPageEntryPointButtonWithDrawer origin={origin} />
					<UpsellButton analyticsOrigin={origin} buttonLabel={messages.tryItFreeButtonLabel} />
				</Flex>
			)}
		</Stack>
	);

	return (
		<JSErrorBoundary
			id="jsmPremiumLandingPageTriggerBanner"
			packageName="jiraServiceDeskPremiumLandingPageTriggerBanner"
			fallback="flag"
		>
			{isAccordionEnabled ? (
				<Pressable
					testId="servicedesk-premium-landing-page-trigger-banner.ui.premium-landing-page-features-accordion"
					onClick={(_, analyticsEvent) => toggleAccordion(analyticsEvent)}
					xcss={containerStyles}
					isDisabled={!isAccordionEnabled}
				>
					{mainContent()}
				</Pressable>
			) : (
				<Flex xcss={containerStyles}>{mainContent()}</Flex>
			)}
		</JSErrorBoundary>
	);
};

const containerStyles = xcss({
	backgroundColor: 'elevation.surface.overlay',
	padding: 'space.200',
	maxWidth: '496px',
});

const contentWrapperStyles = xcss({
	width: '100%',
	alignItems: 'flex-start',
});

const descriptionTextStyles = xcss({
	alignItems: 'flex-start',
	textAlign: 'left',
	width: '100%',
});

const buttonsWrapperStyles = xcss({
	flexWrap: 'wrap-reverse',
	width: '100%',
});

const headingStyles = xcss({
	alignItems: 'flex-start',
	textAlign: 'left',
	width: '100%',
});
